import axios from 'axios';
import { useFilterStore } from '../../stores/filterStore.js';
import { createEnhancedError, errorLogger } from '../../utils/errorHandling.js';
import { appConfig } from '../../config/appConfig.js';

/**
 * HTTP Client Module
 *
 * Centralizes Axios configuration, interceptors, and error handling
 * for all API communication across the application.
 */

/**
 * Determines the API base URL based on environment and runtime context
 * @returns {string} The appropriate API base URL
 */
const getBaseURL = () => {
  // Use explicit environment variable if set
  if (import.meta.env.VITE_API_BASE_URL) {
    return import.meta.env.VITE_API_BASE_URL;
  }

  // Always use relative path - nginx proxy handles routing in Docker
  // In development, Vite proxy handles routing
  return '/api';
};

/**
 * Create Axios instance with base configuration
 */
const httpClient = axios.create({
  baseURL: getBaseURL(),
  timeout: appConfig.api.timeout,
  headers: {
    'Content-Type': 'application/json',
  }
});

/**
 * Request interceptor for automatic filter parameter injection
 * Automatically injects current filter state for mine-related requests
 */
httpClient.interceptors.request.use(
  (config) => {
    // Inject filters for viewport and search requests
    if (config.url?.includes('/mines/viewport') || config.url?.includes('/mines/search')) {
      const { getAPIParams } = useFilterStore.getState();
      const apiParams = getAPIParams();

      // Merge filter parameters with existing params, but preserve existing bbox if set
      config.params = {
        ...config.params,
        ...apiParams.filters
      };

      // Don't override bbox if it's already set manually
      if (config.params.bbox && config.params.bbox.includes('undefined')) {
        console.warn('⚠️ Undefined bbox detected, this should not happen');
      }

      console.log('API Request with filters:', config.params);
    }

    return config;
  },
  (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

/**
 * Response interceptor for enhanced error handling and logging
 */
httpClient.interceptors.response.use(
  (response) => {
    // Log successful responses for debugging
    if (response.config.url?.includes('/mines/viewport')) {
      console.log('API Response:', {
        url: response.config.url,
        filtersApplied: response.data?.meta?.filters_applied,
        resultCount: response.data?.features?.length
      });
    }
    return response;
  },
  (error) => {
    // Create enhanced error with context
    const enhancedError = createEnhancedError(error, {
      url: error.config?.url,
      method: error.config?.method,
      params: error.config?.params,
      timestamp: new Date()
    });

    // Log the enhanced error
    errorLogger.log(enhancedError, {
      source: 'http_client_interceptor'
    });

    return Promise.reject(enhancedError);
  }
);

/**
 * Enhanced error handler for API responses
 * Standardizes error response format across all API calls
 *
 * @param {Error} error - The error to handle
 * @param {Object} context - Additional context for error logging
 * @returns {Object} Standardized error response object
 */
export const handleAPIError = (error, context = {}) => {
  // If it's already an enhanced error, use it
  let enhancedError = error;
  if (!(error instanceof Error && error.name === 'EnhancedError')) {
    enhancedError = createEnhancedError(error, context);
  }

  // Log the error
  errorLogger.log(enhancedError, { source: 'http_client', ...context });

  return {
    success: false,
    error: enhancedError,
    message: enhancedError.message,
    details: enhancedError.details,
    type: enhancedError.type,
    retryable: enhancedError.retryable,
    status: error.response?.status || 0
  };
};

/**
 * Creates a standardized success response object
 *
 * @param {any} data - The response data
 * @param {number} status - HTTP status code
 * @returns {Object} Standardized success response object
 */
export const createSuccessResponse = (data, status = 200) => ({
  success: true,
  data,
  status
});

export default httpClient;